/**
 * La synthèse de la reprise : l'équilibre, ce qui empêche de valider, et les
 * trois gestes qui engagent — valider, revenir sur la validation (motif
 * obligatoire), comptabiliser.
 *
 * ## Comptabiliser n'est pas un clic
 *
 * L'écriture d'ouverture est immuable : une fois passée, une correction est
 * une opération de l'exercice courant, jamais une retouche de l'ouverture. Le
 * bouton demande donc de confirmer, point par point, ce que la règle exige —
 * la situation complète réconciliée, la validation de la comptable — et de
 * taper le mot COMPTABILISER.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Lock, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import {
  comptabiliserLaReprise,
  devaliderLaReprise,
  lireLEcritureDOuverture,
  validerLaReprise,
  type EcritureDOuverture,
} from '../../../lib/compta/reprise/gateway.supabase';
import { bloquants, equilibre, parRubrique, rubriquesSansLigne, SENS } from '../../../lib/compta/reprise/types';
import { dateCourte, euros } from '../components/money';
import type { EtatDeLaReprise } from '../AdminRepriseOuverturePage';

const CONFIRMATIONS = [
  'Les soldes de banque et de caisse sont ceux des extraits et du procès-verbal au 31/12/2025.',
  'Les fournisseurs, dettes sociales et fiscales, subsides et fonds propres viennent de l’état du patrimoine arrêté par la comptable.',
  'La comptable a validé la reprise telle qu’elle est affichée ici.',
] as const;

export default function SyntheseReprise({ etat, apresGeste }: { etat: EtatDeLaReprise; apresGeste: () => void }) {
  const { reprise, synthese, notes, ecartsClients, rubriques, exercice } = etat;
  const [enCours, setEnCours] = useState<null | 'valider' | 'devalider' | 'comptabiliser'>(null);
  const [motif, setMotif] = useState('');
  const [devalidation, setDevalidation] = useState(false);
  const [comptabilisation, setComptabilisation] = useState(false);
  const [coches, setCoches] = useState<boolean[]>(CONFIRMATIONS.map(() => false));
  const [mot, setMot] = useState('');
  const [ecriture, setEcriture] = useState<EcritureDOuverture | null>(null);

  const e = useMemo(() => equilibre(synthese), [synthese]);
  const b = useMemo(() => bloquants({ synthese, notes, ecartsClients }), [synthese, notes, ecartsClients]);
  const groupes = useMemo(() => parRubrique(synthese), [synthese]);
  const vides = useMemo(() => rubriquesSansLigne(rubriques, synthese), [rubriques, synthese]);

  useEffect(() => {
    if (!reprise.ecritureId) { setEcriture(null); return; }
    let vivant = true;
    lireLEcritureDOuverture(reprise.ecritureId)
      .then((x) => { if (vivant) setEcriture(x); })
      .catch((err) => toast.error(messageErreur(err)));
    return () => { vivant = false; };
  }, [reprise.ecritureId]);

  const agir = async (quoi: 'valider' | 'devalider' | 'comptabiliser') => {
    setEnCours(quoi);
    try {
      if (quoi === 'valider') {
        await validerLaReprise(reprise.id);
        toast.success('Reprise validée : ses montants sont arrêtés');
      } else if (quoi === 'devalider') {
        await devaliderLaReprise(reprise.id, motif.trim());
        toast.success('Reprise rouverte en préparation');
        setDevalidation(false);
        setMotif('');
      } else {
        await comptabiliserLaReprise(reprise.id);
        toast.success(`Bilan d’ouverture ${exercice.annee} comptabilisé`);
        setComptabilisation(false);
      }
      apresGeste();
    } catch (err) {
      toast.error(messageErreur(err), { duration: 8000 });
    } finally {
      setEnCours(null);
    }
  };

  const peutComptabiliser = coches.every(Boolean) && mot.trim() === 'COMPTABILISER';

  return (
    <div className="space-y-6">
      <section className="grid gap-4 sm:grid-cols-3">
        <Carte titre="Actif (débit)" valeur={euros(e.debitRetenuCents)} note={`dont validé ${euros(e.debitValideCents)}`} />
        <Carte titre="Passif et fonds propres (crédit)" valeur={euros(e.creditRetenuCents)} note={`dont validé ${euros(e.creditValideCents)}`} />
        <Carte
          titre="Écart"
          valeur={euros(e.ecartRetenuCents)}
          note={e.ecartRetenuCents === 0 ? 'équilibré' : e.ecartRetenuCents > 0 ? 'il manque du passif ou des fonds propres' : 'il manque de l’actif'}
          alerte={e.ecartRetenuCents !== 0}
        />
      </section>
      <p className="-mt-3 text-xs text-gray-500">
        « Retenu » : le montant validé, à défaut le calculé. L’écran ne comble jamais l’écart : il se résout
        par les chiffres de la comptable.
      </p>

      {reprise.statut === 'brouillon' && (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-base font-medium text-gray-900">Avant de valider</h2>
          {b.length === 0 ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Rien ne s’oppose à la validation.
            </p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {b.map((x) => (
                <li key={x.etiquette} className="flex items-start gap-2 text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                  <span>{x.message}</span>
                </li>
              ))}
            </ul>
          )}
          {vides.length > 0 && (
            <p className="mt-3 text-sm text-gray-600">
              Encore aucune ligne pour : {vides.map((r) => r.libelle).join(', ')}. À saisir, ou à écarter si
              l’état du patrimoine n’en porte pas.
            </p>
          )}
          <button
            type="button"
            onClick={() => agir('valider')}
            disabled={b.length > 0 || enCours !== null}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {enCours === 'valider' && <Loader2 className="h-4 w-4 animate-spin" />}
            Valider la reprise
          </button>
        </section>
      )}

      {reprise.statut === 'validee' && (
        <section className="space-y-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p className="flex items-center gap-2 text-sm text-emerald-900">
            <Lock className="h-4 w-4" />
            Validée le {dateCourte(reprise.valideeLe)} : les lignes sont figées. Une note de crédit tardive devient une
            correction de l’exercice courant, jamais un ajustement de l’ouverture.
          </p>

          {!devalidation ? (
            <button type="button" onClick={() => setDevalidation(true)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              <RotateCcw className="h-4 w-4" /> Revenir en préparation
            </button>
          ) : (
            <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3">
              <label className="block text-sm font-medium text-gray-700" htmlFor="motif-devalidation">Pourquoi revenir sur la validation ?</label>
              <textarea id="motif-devalidation" value={motif} onChange={(x) => setMotif(x.target.value)} rows={2}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm" />
              <div className="flex gap-2">
                <button type="button" disabled={motif.trim() === '' || enCours !== null} onClick={() => agir('devalider')}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-700 disabled:opacity-50">
                  Revenir en préparation
                </button>
                <button type="button" onClick={() => { setDevalidation(false); setMotif(''); }}
                  className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
              </div>
            </div>
          )}

          {!comptabilisation ? (
            <button type="button" onClick={() => setComptabilisation(true)}
              className="block rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
              Comptabiliser le bilan d’ouverture…
            </button>
          ) : (
            <div className="space-y-3 rounded-md border border-indigo-300 bg-white p-4">
              <p className="text-sm font-medium text-gray-900">
                Une écriture OUV au 01/01/{exercice.annee}, à N lignes, {euros(e.debitValideCents)} au débit et au crédit.
                Elle sera immuable.
              </p>
              {CONFIRMATIONS.map((c, i) => (
                <label key={c} className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={coches[i]} className="mt-1"
                    onChange={(x) => setCoches(coches.map((v, j) => (j === i ? x.target.checked : v)))} />
                  {c}
                </label>
              ))}
              <label className="block text-sm text-gray-700">
                Taper <strong>COMPTABILISER</strong> pour confirmer
                <input value={mot} onChange={(x) => setMot(x.target.value)}
                  className="mt-1 block w-60 rounded border border-gray-300 px-2 py-1 text-sm" />
              </label>
              <div className="flex gap-2">
                <button type="button" disabled={!peutComptabiliser || enCours !== null} onClick={() => agir('comptabiliser')}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                  {enCours === 'comptabiliser' && <Loader2 className="h-4 w-4 animate-spin" />}
                  Comptabiliser
                </button>
                <button type="button" onClick={() => { setComptabilisation(false); setMot(''); setCoches(CONFIRMATIONS.map(() => false)); }}
                  className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
              </div>
            </div>
          )}
        </section>
      )}

      {reprise.statut === 'comptabilisee' && (
        <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
          <p className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Comptabilisée le {dateCourte(reprise.comptabiliseeLe)}
            {ecriture && (
              <> : écriture OUV n° {ecriture.numero ?? '—'} du {dateCourte(ecriture.dateEcriture)},
                {' '}{ecriture.nbLignes} lignes, {euros(ecriture.totalCents)} au débit et au crédit</>
            )}.
          </p>
          <p className="mt-1">L’ouverture ne bouge plus : toute correction passe par une opération de l’exercice courant.</p>
        </section>
      )}

      <section className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Rubrique / compte</th>
              <th className="px-3 py-2">Sens</th>
              <th className="px-3 py-2 text-right">Lignes</th>
              <th className="px-3 py-2 text-right">À traiter</th>
              <th className="px-3 py-2 text-right">Calculé</th>
              <th className="px-3 py-2 text-right">Validé</th>
              <th className="px-3 py-2 text-right">Retenu</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groupes.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                Aucune ligne : calculer la proposition (onglet « Clients et familles ») et saisir les soldes.
              </td></tr>
            )}
            {groupes.map((g) => (
              <RubriqueLignes key={g.rubrique} groupe={g} />
            ))}
          </tbody>
          {groupes.length > 0 && (
            <tfoot className="bg-gray-50 font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={6}>Actif (débit)</td>
                <td className="px-3 py-2 text-right tabular-nums">{euros(e.debitRetenuCents)}</td>
              </tr>
              <tr>
                <td className="px-3 py-2" colSpan={6}>Passif et fonds propres (crédit)</td>
                <td className="px-3 py-2 text-right tabular-nums">{euros(e.creditRetenuCents)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </section>
    </div>
  );
}

function RubriqueLignes({ groupe }: { groupe: ReturnType<typeof parRubrique>[number] }) {
  return (
    <>
      <tr className="bg-gray-50/60">
        <td className="px-3 py-2 font-medium text-gray-900" colSpan={3}>
          {groupe.libelle}
          <span className="ml-2 text-xs font-normal text-gray-500">{groupe.calculee ? 'calculée (familles) et saisie (hors CDV)' : 'saisie'}</span>
        </td>
        <td className={clsx('px-3 py-2 text-right tabular-nums', groupe.aTraiter > 0 && 'text-amber-700')}>{groupe.aTraiter || ''}</td>
        <td colSpan={3} />
      </tr>
      {groupe.lignes.map((l) => (
        <tr key={`${l.compteCode}-${l.sens}`}>
          <td className="px-3 py-1.5 pl-6 text-gray-700">
            {l.compteCode} {l.compteLibelle}
            {l.totalEteintCents > 0 && (
              <span className="ml-2 text-xs text-gray-500">dont {euros(l.totalEteintCents)} éteints avant l’ouverture, hors ouverture</span>
            )}
          </td>
          <td className="px-3 py-1.5 text-gray-600">{SENS[l.sens]}</td>
          <td className="px-3 py-1.5 text-right tabular-nums">{l.nbLignes}</td>
          <td className={clsx('px-3 py-1.5 text-right tabular-nums', (l.nbAValider + l.nbARevoir + l.nbEnAttente) > 0 && 'text-amber-700')}>
            {l.nbAValider + l.nbARevoir + l.nbEnAttente || ''}
          </td>
          <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{l.nbCalculees > 0 ? euros(l.totalCalculeCents) : ''}</td>
          <td className="px-3 py-1.5 text-right tabular-nums">{euros(l.totalValideCents)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums font-medium">{euros(l.totalRetenuCents)}</td>
        </tr>
      ))}
    </>
  );
}

function Carte({ titre, valeur, note, alerte }: { titre: string; valeur: string; note: string; alerte?: boolean }) {
  return (
    <div className={clsx('rounded-lg border bg-white p-4', alerte ? 'border-amber-300' : 'border-gray-200')}>
      <p className="text-xs uppercase tracking-wide text-gray-500">{titre}</p>
      <p className={clsx('mt-1 text-xl font-semibold tabular-nums', alerte ? 'text-amber-700' : 'text-gray-900')}>{valeur}</p>
      <p className="mt-1 text-xs text-gray-500">{note}</p>
    </div>
  );
}
